import React from 'react';
import { Check, Heart, Copy, Mail, MessageSquare, MessageCircle } from 'lucide-react';
import { WeddingInvitation } from '../../../types/invitation';
import { BasicInfo } from '../types';

interface WizardSuccessScreenProps {
  basicInfo: BasicInfo;
  generatedCode: string;
  createdInvitation: WeddingInvitation;
  onComplete: (invitation: WeddingInvitation) => void;
}

export const WizardSuccessScreen: React.FC<WizardSuccessScreenProps> = ({
  basicInfo,
  generatedCode,
  createdInvitation,
  onComplete,
}) => {
  const copyInviteLink = () => {
    const link = `${window.location.origin}/invite/${generatedCode}`;
    navigator.clipboard.writeText(link);
  };

  const shareViaLine = () => {
    const link = `${window.location.origin}/invite/${generatedCode}`;
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(link)}`, '_blank');
  };

  const shareViaWhatsApp = () => {
    const link = `${window.location.origin}/invite/${generatedCode}`;
    const message = `${basicInfo.brideName} & ${basicInfo.groomName} 的婚禮邀請函 💕\n\n我們誠摯邀請您參加我們的婚禮慶典！\n\n${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleGoToDashboard = () => {
    onComplete(createdInvitation);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          🎉 邀請函建立成功！
        </h2>
        <p className="text-gray-600 mb-8">
          您的婚禮邀請函已經準備就緒，現在可以分享給親朋好友了
        </p>

        <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-6 mb-8 border border-pink-200">
          <div className="text-center">
            <Heart className="h-8 w-8 text-pink-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {basicInfo.brideName} & {basicInfo.groomName}
            </h3>
            <p className="text-sm text-gray-600 mb-1">
              📅 {basicInfo.date} {basicInfo.time}
            </p>
            <p className="text-sm text-gray-600">
              📍 {basicInfo.venue.name}
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            邀請連結
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={`${window.location.origin}/invite/${generatedCode}`}
              readOnly
              className="flex-1 border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-sm"
            />
            <button
              onClick={copyInviteLink}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm flex items-center"
            >
              <Copy className="h-4 w-4 mr-1" />
              複製
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <button
            onClick={shareViaLine}
            className="bg-[#06C755] hover:bg-[#05b34c] text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            LINE
          </button>
          <button
            onClick={shareViaWhatsApp}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            WhatsApp
          </button>
          <button
            onClick={() => window.open(`mailto:?subject=${basicInfo.brideName} & ${basicInfo.groomName} 的婚禮邀請&body=我們誠摯邀請您參加我們的婚禮慶典！請點擊連結查看詳情：${window.location.origin}/invite/${generatedCode}`, '_blank')}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg flex items-center justify-center transition-colors"
          >
            <Mail className="h-4 w-4 mr-2" />
            郵件
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => window.open(`/invite/${generatedCode}`, '_blank')}
            className="bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg font-medium"
          >
            預覽邀請函
          </button>
          <button
            onClick={handleGoToDashboard}
            className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-medium"
          >
            前往管理面板
          </button>
        </div>
      </div>
    </div>
  );
};
